import { useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { FiArrowLeft, FiCheckCircle, FiMail, FiPhone, FiUser } from "react-icons/fi";
import Card from "components/Card";
import AppBreadcrumbs from "components/common/AppBreadcrumbs";
import StatusBadge from "components/common/StatusBadge";
import { formatTs } from "utils/helper";

function InfoRow({ label, value }) {
  if (!value && value !== false) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-500">{label}</p>
      <p className="text-sm font-medium text-text-900">{String(value)}</p>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-text-500">{title}</h3>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function UserAvatar({ src, initials }) {
  const [imgFailed, setImgFailed] = useState(false);

  if (src && !imgFailed) {
    return (
      <img
        src={src}
        alt="Profile"
        onError={() => setImgFailed(true)}
        className="h-16 w-16 shrink-0 rounded-full object-cover border-2 border-slate-200 shadow-sm"
      />
    );
  }

  return (
    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xl font-bold text-brand-700 border-2 border-brand-200 shadow-sm">
      {initials || <FiUser className="h-6 w-6" />}
    </div>
  );
}

export default function UserDetailsPage() {
  const { userId } = useParams();
  const { state } = useLocation();
  const user = state?.user;

  if (!user) {
    return <Navigate to="/users" replace />;
  }

  const isCorporate = user.investorType === "corporate";
  const isIndividual = user.investorType === "individual";

  const profile = isIndividual
    ? (user.individualProfile ?? user.entityProfile ?? {})
    : isCorporate
      ? (user.corporateProfile ?? user.entityProfile ?? {})
      : (user.individualProfile ?? user.corporateProfile ?? user.entityProfile ?? {});

  const address = profile.residentialAddress ?? profile.registeredAddress ?? {};

  const fullName =
    (
      user.full_name ??
      user.fullName ??
      user.name ??
      [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim()
    ) ||
    user.email?.split("@")[0] ||
    "—";
  const displayName = isCorporate && profile.companyName ? profile.companyName : fullName;

  const initials = [profile.firstName?.[0], profile.lastName?.[0]]
    .filter(Boolean)
    .join("")
    .toUpperCase() || user.email?.[0]?.toUpperCase() || "";

  const isVerified = Boolean(user.emailVerifiedAt);

  const beneficialOwners = isCorporate && Array.isArray(profile.beneficialOwners)
    ? profile.beneficialOwners.filter(Boolean)
    : [];

  const avatarSrc =
    user.profileImage ??
    user.profileImageUrl ??
    user.avatarUrl ??
    user.avatar ??
    user.imageUrl ??
    user.picture ??
    profile.profileImage ??
    profile.imageUrl ??
    null;

  return (
    <div className="space-y-6">
      <AppBreadcrumbs
        items={[
          { label: "Users", path: "/users" },
          { label: displayName !== "—" ? displayName : user.email, path: `/users/${userId}` },
        ]}
      />

      {/* Header */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <UserAvatar src={avatarSrc} initials={initials} />
            <div>
              <h2 className="text-xl font-bold text-text-900">{fullName}</h2>
              <p className="mt-0.5 flex items-center gap-1.5 text-sm text-text-600">
                <FiMail className="h-4 w-4 shrink-0" />
                {user.email}
                {isVerified && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                    <FiCheckCircle className="h-3.5 w-3.5" />
                    Verified
                  </span>
                )}
              </p>
              {user.phone && (
                <p className="mt-0.5 flex items-center gap-1.5 text-sm text-text-600">
                  <FiPhone className="h-4 w-4 shrink-0" />
                  {user.phone}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge as="span" status={user.status } />
            <Link
              to="/users"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-700 transition hover:bg-slate-50"
            >
              <FiArrowLeft className="h-3.5 w-3.5" />
              Back to list
            </Link>
          </div>
        </div>
      </Card>
  {/* Personal Information */} 
        <SectionCard title=" Personal Information">
          {isCorporate && <InfoRow label="Company Name" value={profile.companyName} />}
          <InfoRow label="First Name" value={profile.firstName} />
          <InfoRow label="Last Name" value={profile.lastName} />
          <InfoRow label="Age Confirmed (19+)" value={profile.isOver19Confirmed ? "Yes" : "No"} />
        </SectionCard>

      {/* Account Info */}
      <SectionCard title="Account Information">
        <InfoRow label="User ID" value={user._id ?? user.id} />
        <InfoRow label="Email" value={user.email} />
        <InfoRow label="Phone" value={user.phone} />
        <InfoRow label="Role" value={user.role} />
        <InfoRow label="Investor Type" value={user.investorType} />
        <InfoRow label="Email Verified At" value={user.emailVerifiedAt ? formatTs(user.emailVerifiedAt) : "Not verified"} />
        <InfoRow label="Created At" value={formatTs(user.createdAt)} />
        <InfoRow label="Updated At" value={formatTs(user.updatedAt)} />
      </SectionCard>

    
     

      {/* Address */}
      {Object.keys(address).length > 0 && (
        <SectionCard title={isCorporate ? "Registered Address" : "Residential Address"}>
          <InfoRow label="Address Line 1" value={address.line1} />
          <InfoRow label="City" value={address.city} />
          <InfoRow label="Province" value={address.province} />
          <InfoRow label="Country" value={address.country} />
          <InfoRow label="Postal Code" value={address.postalCode} />
        </SectionCard>
      )}

      {/* Beneficial Owners */}
      {beneficialOwners.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-500">
            Beneficial Owners
          </h3>
          <div className="space-y-2">
            {beneficialOwners.map((owner, index) => (
              <div
                key={`beneficial-owner-${index}`}
                className="grid grid-cols-[180px_1fr] items-center gap-4 rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-2.5"
              >
                <span className="text-sm font-semibold text-text-700">
                  Beneficial Owner {index + 1}
                </span>
                <span className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-text-900">
                  {owner}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
